import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Tooltip } from "@agronod/mui-components";
import {
  Edit as EditIcon,
  Block as BlockIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { AdminService } from "@/services/admin.service";
import { AdminUser, UserRole } from "@/types";
import { enqueueSnackbar } from "notistack";

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Statistics
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter((u) => u.isActive).length,
    adminUsers: users.filter((u) => u.role !== UserRole.AGROKOLL_SYSTEM_ADMIN)
      .length,
    newThisMonth: users.filter((u) => {
      const createdDate = new Date(u.createdAt);
      const now = new Date();
      return (
        createdDate.getMonth() === now.getMonth() &&
        createdDate.getFullYear() === now.getFullYear()
      );
    }).length,
  };

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AdminService.getUsers({ limit: 100 });
      setUsers(response.items);
    } catch (err: any) {
      console.error("Failed to load users:", err);
      setError(err.message || "Kunde inte ladda användare");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await AdminService.updateUserStatus(userId, !currentStatus);
      enqueueSnackbar(
        currentStatus ? "Användare blockerad" : "Användare aktiverad",
        { variant: "success" }
      );
      loadUsers();
    } catch (err: any) {
      console.error("Failed to update user status:", err);
      enqueueSnackbar(err.message || "Kunde inte uppdatera användarstatus", {
        variant: "error",
      });
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("sv-SE");
  };

  const getRoleLabel = (role: UserRole): string => {
    const labels: Record<UserRole, string> = {
      [UserRole.AGROKOLL_ADMIN]: "Admin",
      [UserRole.AGROKOLL_OWNER]: "Ägare",
      [UserRole.AGROKOLL_RADGIVARE]: "Rådgivare",
      [UserRole.AGROKOLL_B2B]: "B2B Admin",
      [UserRole.AGROKOLL_SYSTEM_ADMIN]: "System Admin",
    };
    return labels[role];
  };

  const getRoleColor = (role: UserRole) => {
    const colors: Record<UserRole, "default" | "primary" | "secondary" | "error"> = {
      [UserRole.AGROKOLL_ADMIN]: "default",
      [UserRole.AGROKOLL_OWNER]: "default",
      [UserRole.AGROKOLL_RADGIVARE]: "secondary",
      [UserRole.AGROKOLL_B2B]: "primary",
      [UserRole.AGROKOLL_SYSTEM_ADMIN]: "error",
    };
    return colors[role];
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ bgcolor: "background.page", minHeight: "100vh", py: 3 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px"
            }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.page", minHeight: "100vh", py: 3 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3
          }}>
          <Typography variant="h4" component="h1">
            Användarhantering
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            size="large"
            onClick={() => navigate("/admin/users/new")}
          >
            Skapa användare
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{
          mb: 4
        }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Totalt användare
                </Typography>
                <Typography variant="h4">{stats.totalUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Aktiva användare
                </Typography>
                <Typography variant="h4" sx={{
                  color: "success.main"
                }}>
                  {stats.activeUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Administratörer
                </Typography>
                <Typography variant="h4" sx={{
                  color: "warning.main"
                }}>
                  {stats.adminUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Nya denna månaden
                </Typography>
                <Typography variant="h4" sx={{
                  color: "info.main"
                }}>
                  {stats.newThisMonth}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Users Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{
              mb: 2
            }}>
              Användare
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Användare</TableCell>
                    <TableCell>Gård</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Roll</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Senaste inloggning</TableCell>
                    <TableCell align="center">Åtgärder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2
                          }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {user.email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{
                              fontWeight: "medium"
                            }}>
                              {user.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary"
                              }}
                            >
                              Registrerad {formatDate(user.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.farmProfile?.farmName || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.farmProfile?.region || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(user.role)}
                          size="small"
                          color={getRoleColor(user.role)}
                          {...(user.role !== UserRole.AGROKOLL_OWNER && user.role !== UserRole.AGROKOLL_ADMIN && {
                            icon: <AdminIcon />,
                          })}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? "Aktiv" : "Blockerad"}
                          size="small"
                          color={user.isActive ? "success" : "error"}
                          variant={user.isActive ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {user.lastLogin
                            ? formatDate(user.lastLogin)
                            : "Aldrig"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center"
                          }}>
                          <Tooltip title="Redigera användare">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(`/admin/users/${user.id}/edit`)
                              }
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Återställ lösenord">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() =>
                                navigate(`/admin/users/${user.id}/edit`)
                              }
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              user.isActive
                                ? "Blockera användare"
                                : "Aktivera användare"
                            }
                          >
                            <IconButton
                              size="small"
                              color={user.isActive ? "error" : "success"}
                              onClick={() =>
                                handleToggleStatus(user.id, user.isActive)
                              }
                            >
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminUsersPage;
